import { ArrowRight, Bell, Receipt, ShieldCheck } from 'lucide-react'
import { Link, Navigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/lib/auth-client'

export function IndexPage() {
	const session = useSession()

	if (session.isPending) {
		return (
			<section className="container mx-auto max-w-3xl px-4 py-16">
				<Skeleton className="h-10 w-2/3" />
				<Skeleton className="mt-4 h-6 w-1/2" />
			</section>
		)
	}

	if (session.data) {
		return <Navigate to="/app" replace />
	}

	return (
		<div className="relative isolate overflow-hidden">
			<div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary/[0.04] via-transparent to-transparent" />
			<section className="container mx-auto flex max-w-4xl flex-col items-center gap-8 px-4 py-20 text-center sm:py-28">
				<div className="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
					<Receipt className="size-6" />
				</div>
				<h1 className="max-w-2xl text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
					Every receipt, every warranty — in one calm place.
				</h1>
				<p className="max-w-xl text-balance text-lg text-muted-foreground">
					Snap a photo or drop a PDF, and Warrantly remembers what you bought,
					what's still covered, and when to act before the warranty runs out.
				</p>
				<div className="flex flex-col items-center gap-3 sm:flex-row">
					<Button asChild size="lg">
						<Link to="/login">
							Sign in
							<ArrowRight className="size-4" />
						</Link>
					</Button>
					<Button asChild variant="outline" size="lg">
						<Link to="/register">Create an account</Link>
					</Button>
				</div>

				<div className="mt-12 grid grid-cols-1 gap-4 text-left sm:grid-cols-3">
					<Feature
						icon={<Receipt className="size-5" />}
						title="Catalog everything"
						body="Upload a picture or PDF and tag it with your own categories."
					/>
					<Feature
						icon={<ShieldCheck className="size-5" />}
						title="Track warranties"
						body="See at a glance how much coverage is left on every item."
					/>
					<Feature
						icon={<Bell className="size-5" />}
						title="Never miss expiry"
						body="Choose lead time and we'll remind you before the warranty ends."
					/>
				</div>
			</section>
		</div>
	)
}

function Feature({
	icon,
	title,
	body,
}: {
	icon: React.ReactNode
	title: string
	body: string
}) {
	return (
		<Card className="gap-2 py-5">
			<CardContent className="space-y-2">
				<div className="flex size-9 items-center justify-center rounded-md bg-muted text-foreground">
					{icon}
				</div>
				<p className="font-medium">{title}</p>
				<p className="text-sm text-muted-foreground">{body}</p>
			</CardContent>
		</Card>
	)
}
